import { LocalIntelligence as LocalIntelligenceType } from "@/features/planner/api/planner";

interface LocalIntelligenceProps {
    data?: LocalIntelligenceType;
}

export const LocalIntelligence = ({ data }: LocalIntelligenceProps) => {
    if (!data) {
        return (
            <div className="max-w-[1000px] mx-auto p-10 bg-white flex flex-col items-center justify-center min-h-[400px]">
                <h2 className="text-center text-[28px] font-bold text-[#187A85] mb-6 font-serif">
                    Local Intelligence
                </h2>
                <p className="text-[#6D6D6D] text-lg">No local intelligence information available.</p>
            </div>
        );
    }

    return (
        <div className="mx-5 p-10 bg-white">
            <h2 className="text-center text-[28px] font-bold text-[#187A85] mb-6 font-serif">
                Local Intelligence
            </h2>

            {/* Top Row: Location & Practical */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {/* Location Details */}
                <div className="bg-[#FFF9F5] rounded-xl p-6">
                    <h4 className="text-[16px] font-bold text-[#E76828] mb-4">Location Details</h4>
                    <div className="flex flex-col gap-2">
                        <InfoRow label="Country" value={data.country} />
                        <div className="flex justify-between items-center py-2 border-b border-[#EAEAEA] last:border-0 text-[14px]">
                            <span className="text-[#222222]">Flag</span>
                            <span className="text-[#222222] text-right">
                                {data.flag_url && (
                                    <img
                                        src={data.flag_url}
                                        alt="Flag"
                                        className="h-6 w-auto rounded shadow-sm"
                                    />
                                )}
                            </span>
                        </div>

                        <InfoRow label="Region" value={data.region} />
                        <InfoRow label="Capital" value={data.capital} />
                        <InfoRow label="Population" value={data.population ? data.population.toLocaleString() : undefined} />
                    </div>
                </div>

                {/* Practical Info */}
                <div className="bg-[#FFF9F5] rounded-xl p-6 relative">
                    <div className="flex justify-between items-start mb-4">
                        <h4 className="text-[16px] font-bold text-[#E76828]">Practical Info</h4>
                    </div>
                    <div className="flex flex-col gap-2">
                        <InfoRow label="Timezone" value={data.timezone} />
                        <InfoRow label="Currency" value={data.currency ? `${data.currency.code} (${data.currency.symbol})` : undefined} />
                        <InfoRow label="Calling Code" value={data.calling_code} />
                        <InfoRow label="Driving" value={data.driving_side ? `${data.driving_side} Side` : undefined} />
                    </div>
                </div>
            </div>

            {/* Mobile Operators */}
            <div className="bg-[#FFF9F5] rounded-xl p-6 mb-4">
                <h4 className="text-[16px] font-bold text-[#E76828] mb-2">Mobile Operators</h4>
                <div className="flex justify-between items-center text-[14px]">
                    <span className="text-[#222222]">Available Networks</span>
                    <span className="font-bold text-[#222222] text-right">{data.mobile_operators?.join(", ") || "N/A"}</span>
                </div>
            </div>

            {/* Emergency Contacts */}
            <div className="bg-[#FFF9F5] rounded-xl p-6 mb-4">
                <h4 className="text-[16px] font-bold text-[#E76828] mb-4">Emergency Contacts</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <EmergencyCard label="Universal Emergency" number={data.emergency_numbers?.universal} />
                    <EmergencyCard label="Police" number={data.emergency_numbers?.police} />
                    <EmergencyCard label="Ambulance" number={data.emergency_numbers?.ambulance} />
                    <EmergencyCard label="Fire" number={data.emergency_numbers?.fire} />
                </div>
            </div>

            {/* Bottom Row: Safety & Health */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {/* Safety Indices */}
                <div className="bg-[#FFF9F5] rounded-xl p-6">
                    <h4 className="text-[16px] font-bold text-[#E76828] mb-4">Safety Indices</h4>
                    <div className="flex flex-col gap-2">
                        <InfoRow label="Safety Level" value={data.safety_indices?.safety_level} />
                        <InfoRow label="Safety Score" value={data.safety_indices?.safety_score} />
                        <InfoRow label="Risk" value={data.safety_indices?.risk_level} />
                    </div>
                </div>

                {/* Common Risks */}
                <div className="bg-[#FFF9F5] rounded-xl p-6">
                    <h4 className="text-[16px] font-bold text-[#E76828] mb-4">Common Risks</h4>
                    {data.safety_indices?.common_risks && data.safety_indices.common_risks.length > 0 ? (
                        <ul className="list-disc ml-4 text-[14px] text-[#222222] space-y-1">
                            {data.safety_indices.common_risks.map((risk, index) => (
                                <li key={index}>{risk}</li>
                            ))}
                        </ul>
                    ) : (
                        <span className="text-[14px] text-[#222222]">No specific risks reported.</span>
                    )}
                </div>
            </div>

            {/* Health Information */}
            <div className="bg-[#FFF9F5] rounded-xl p-6">
                <h4 className="text-[16px] font-bold text-[#E76828] mb-4">Health Information</h4>
                <div className="flex flex-col gap-2">
                    <InfoRow
                        label="Recommended"
                        value={data.health_advisories?.vaccinations_recommended?.join(", ")}
                    />
                    <InfoRow
                        label="Water Safety"
                        value={data.health_advisories?.water_safety}
                    />
                    {data.health_advisories?.food_safety_concerns && data.health_advisories.food_safety_concerns.length > 0 && (
                        <InfoRow
                            label="Food Safety Concerns (Be careful about these foods)"
                            value={data.health_advisories.food_safety_concerns.join(", ")}
                        />
                    )}
                    <InfoRow
                        label="Medical Facilities"
                        value={data.health_advisories?.medical_facilities}
                    />
                </div>
            </div>
        </div>
    );
};

const InfoRow = ({ label, value }: { label: string; value: string | number | undefined }) => (
    <div className="flex justify-between items-center py-2 border-b border-[#EAEAEA] last:border-0 text-[14px]">
        <span className="text-[#222222]">{label}</span>
        <span className="text-[#222222] text-right">{value ?? "N/A"}</span>
    </div>
);

const EmergencyCard = ({ label, number }: { label: string; number?: string }) => (
    <div className="bg-white rounded-lg px-4 py-3 flex justify-between items-center">
        <span className="text-[14px] text-[#222222]">{label}</span>
        <span className="text-[16px] font-bold text-[#E76828]">{number ?? "N/A"}</span>
    </div>
);
